import React, { useState, useEffect, useRef } from 'react';
import './App.css';

export const ALLOWED_MEASUREMENT_LINE_COLUMNS = [
  'Answer_Checkbox_Value__c',
  'Answer_Percentage_Value__c',
  'Answer_Date_Value__c',
  'Answer_Number_Value__c',
  'Answer_Number_With_Decimal__c',
  'Answer_Dynamic_Picklist_Value__c',
  'Text__c',
  'available__c',
  'Available_Ambient__c',
  'Available_Chilled__c',
  'Facings__c',
  'Facings_ambient__c',
  'Facings_chilled__c',
  'Metres__c',
  'Metres_ambient__c',
  'Metres_chilled__c',
  'Percentage__c',
  'Number__c',
  'Number_of_cases__c',
  'Number_of_displays__c',
  'Price__c',
  'Commercialised__c',
  'Introduced__c',
  'Decimal_Number__c',
  'Checkbox__c',
  'Facings_chilled_in_CCE_coolers__c',
  'Compliant_c__c',
  'Compliant__c',
];

const ALLOWED_MEASUREMENT_LINE_COLUMNS_SET = new Set(
  ALLOWED_MEASUREMENT_LINE_COLUMNS.map((col) => col.toLowerCase())
);

// Helper to parse CSV line into columns, handling quoted tokens
export const parseCsvLine = (line) => {
  const cols = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      cols.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  cols.push(current.trim());
  return cols;
};

export const cleanToken = (token) => {
  let t = token.trim();
  if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
    t = t.slice(1, -1).trim();
  }
  return t;
};

export const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return Number(num).toLocaleString();
};

function App() {
  const [measurementFile, setMeasurementFile] = useState(null);
  const [crmFile, setCrmFile] = useState(null);
  const [measurementRows, setMeasurementRows] = useState(null);
  const [crmRows, setCrmRows] = useState(null);
  const [crmError, setCrmError] = useState('');
  const [measurementError, setMeasurementError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [action, setAction] = useState('');
  const [message, setMessage] = useState('');
  const [fileInfo, setFileInfo] = useState(''); // New state for file info

  const [crmProgress, setCrmProgress] = useState({
    isAnalyzing: false,
    percent: 0,
    stageText: '',
    fileName: '',
    fileSizeText: '',
  });

  const [measurementProgress, setMeasurementProgress] = useState({
    isAnalyzing: false,
    percent: 0,
    stageText: '',
    fileName: '',
    fileSizeText: '',
  });

  const crmAbortRef = useRef(false);
  const measurementAbortRef = useRef(false);
  const crmReaderRef = useRef(null);
  const measurementReaderRef = useRef(null);
  const crmInputRef = useRef(null);
  const measurementInputRef = useRef(null);

  // The "Run Rescore" button is active ONLY when:
  // 1. At least one file is provided (crmFile or measurementFile)
  // 2. Neither file has a validation error
  // 3. A rescoring action has been selected
  // 4. Neither file is currently analyzing
  const isAnalyzing = crmProgress.isAnalyzing || measurementProgress.isAnalyzing;
  const hasValidFiles = Boolean(crmFile || measurementFile) && !crmError && !measurementError;
  const isButtonDisabled = !hasValidFiles || !action || isAnalyzing;

  const handleCancelCrmAnalysis = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    crmAbortRef.current = true;
    if (crmReaderRef.current) {
      try {
        crmReaderRef.current.abort();
      } catch (err) {
        // ignore
      }
    }
    setCrmProgress({ isAnalyzing: false, percent: 0, stageText: '', fileName: '', fileSizeText: '' });
    setCrmFile(null);
    setCrmRows(null);
    setCrmError('');
    if (crmInputRef.current) {
      crmInputRef.current.value = '';
    }
    const measPart = measurementFile ? `${measurementFile.name} (${measurementRows ?? '...'} rows)` : '';
    setFileInfo(measPart);
  };

  const handleCancelMeasurementAnalysis = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    measurementAbortRef.current = true;
    if (measurementReaderRef.current) {
      try {
        measurementReaderRef.current.abort();
      } catch (err) {
        // ignore
      }
    }
    setMeasurementProgress({ isAnalyzing: false, percent: 0, stageText: '', fileName: '', fileSizeText: '' });
    setMeasurementFile(null);
    setMeasurementRows(null);
    setMeasurementError('');
    if (measurementInputRef.current) {
      measurementInputRef.current.value = '';
    }
    const crmPart = crmFile ? `${crmFile.name} (${crmRows ?? '...'} rows)` : '';
    setFileInfo(crmPart);
  };

  const handleMeasurementFileChange = (event) => {
    const inputElement = event.target;
    const uploadedFile = inputElement.files && inputElement.files[0];

    if (!uploadedFile) {
      handleCancelMeasurementAnalysis();
      return;
    }

    measurementAbortRef.current = true;
    if (measurementReaderRef.current) {
      try {
        measurementReaderRef.current.abort();
      } catch (err) {
        // ignore
      }
    }

    measurementAbortRef.current = false;
    setMeasurementFile(null);
    setMeasurementRows(null);
    setMeasurementError('');
    setMeasurementProgress({
      isAnalyzing: true,
      percent: 5,
      stageText: 'Reading file...',
      fileName: uploadedFile.name,
      fileSizeText: formatBytes(uploadedFile.size),
    });
    setFileInfo(`Analyzing ${uploadedFile.name}...`);

    const reader = new FileReader();
    measurementReaderRef.current = reader;

    reader.onprogress = (e) => {
      if (measurementAbortRef.current) return;
      if (e.lengthComputable && e.total > 0) {
        const readPercent = Math.min(30, Math.round((e.loaded / e.total) * 30));
        setMeasurementProgress((prev) => ({
          ...prev,
          percent: Math.max(prev.percent, readPercent),
          stageText: `Reading file (${formatBytes(e.loaded)} / ${formatBytes(e.total)})...`,
        }));
      }
    };

    reader.onerror = () => {
      if (measurementAbortRef.current) return;
      setMeasurementProgress({ isAnalyzing: false, percent: 0, stageText: '', fileName: '', fileSizeText: '' });
      setMeasurementError('Failed to read the file.');
      setMessage('Failed to read the file.');
    };

    reader.onload = async (e) => {
      if (measurementAbortRef.current) return;
      setMeasurementProgress((prev) => ({
        ...prev,
        percent: 32,
        stageText: 'Validating header...',
      }));
      await new Promise((r) => setTimeout(r, 0));
      if (measurementAbortRef.current) return;

      const content = e.target.result || '';
      const sanitizedContent = content.replace(/^\uFEFF/, '');
      const lines = sanitizedContent.split(/\r\n|\n|\r/).filter((row) => row.trim() !== '');

      // Validate:
      // 1. Must contain at least a header row
      // 2. Header must contain mandatory 'uuid' column (case-insensitive)
      // 3. Header must contain at least one allowed measurement column (case-insensitive)
      // 4. All columns in header must be either 'uuid' or in the allowed measurement columns list
      // 5. All data rows must have the same number of columns as the header
      let isHeaderValid = false;
      let expectedColCount = 0;
      if (lines.length > 0) {
        const rawHeaderCols = parseCsvLine(lines[0]);
        expectedColCount = rawHeaderCols.length;
        const headerCols = rawHeaderCols.map((col) => cleanToken(col).toLowerCase());

        const hasUuid = headerCols.includes('uuid');
        const hasAtLeastOneAllowed = headerCols.some((col) =>
          ALLOWED_MEASUREMENT_LINE_COLUMNS_SET.has(col)
        );
        const allColumnsValid = headerCols.every(
          (col) => col === 'uuid' || ALLOWED_MEASUREMENT_LINE_COLUMNS_SET.has(col)
        );

        if (hasUuid && hasAtLeastOneAllowed && allColumnsValid) {
          isHeaderValid = true;
        }
      }

      if (!isHeaderValid) {
        // Reject the file
        setMeasurementFile(null);
        setMeasurementRows(null);
        if (measurementInputRef.current) {
          measurementInputRef.current.value = '';
        }
        const errorMsg =
          'Invalid Measurement Lines CSV: The expected format requires a mandatory "uuid" column and at least one valid measurement column.';
        setMessage(errorMsg);
        setMeasurementError(errorMsg);
        setMeasurementProgress({ isAnalyzing: false, percent: 0, stageText: '', fileName: '', fileSizeText: '' });
        const crmPart = crmFile ? `${crmFile.name} (${crmRows ?? '...' } rows)` : '';
        setFileInfo(crmPart);
        return;
      }

      // Header is valid, now validate data rows in non-blocking batches
      const totalRows = lines.length - 1;
      let allRowsMatch = true;
      const BATCH_SIZE = 5000;

      for (let i = 1; i < lines.length; i += BATCH_SIZE) {
        if (measurementAbortRef.current) return;
        const batchEnd = Math.min(lines.length, i + BATCH_SIZE);
        for (let j = i; j < batchEnd; j++) {
          if (parseCsvLine(lines[j]).length !== expectedColCount) {
            allRowsMatch = false;
            break;
          }
        }
        if (!allRowsMatch) break;

        const processed = batchEnd - 1;
        const progressPct = totalRows > 0 ? 35 + Math.round((processed / totalRows) * 60) : 95;
        setMeasurementProgress((prev) => ({
          ...prev,
          percent: Math.min(95, progressPct),
          stageText:
            totalRows > 1000
              ? `Validating row ${formatNumber(processed)} of ${formatNumber(totalRows)}...`
              : 'Validating rows...',
        }));

        if (batchEnd < lines.length) {
          await new Promise((r) => setTimeout(r, 0));
        }
      }

      if (measurementAbortRef.current) return;

      if (!allRowsMatch) {
        // Reject the file
        setMeasurementFile(null);
        setMeasurementRows(null);
        if (measurementInputRef.current) {
          measurementInputRef.current.value = '';
        }
        const errorMsg =
          'Invalid Measurement Lines CSV: The expected format requires a mandatory "uuid" column and at least one valid measurement column.';
        setMessage(errorMsg);
        setMeasurementError(errorMsg);
        setMeasurementProgress({ isAnalyzing: false, percent: 0, stageText: '', fileName: '', fileSizeText: '' });
        const crmPart = crmFile ? `${crmFile.name} (${crmRows ?? '...' } rows)` : '';
        setFileInfo(crmPart);
        return;
      }

      // Valid file
      setMeasurementProgress({ isAnalyzing: false, percent: 100, stageText: 'Complete', fileName: '', fileSizeText: '' });
      setMeasurementFile(uploadedFile);
      setMeasurementError('');
      setMessage('');
      setMeasurementRows(totalRows);
      const crmPart = crmFile ? `${crmFile.name} (${crmRows ?? '...' } rows)` : '';
      setFileInfo(`${uploadedFile.name} loaded with ${totalRows} rows${crmPart ? ' · ' + crmPart : ''}`);
    };

    reader.readAsText(uploadedFile);
  };

  const handleCrmFileChange = (event) => {
    const inputElement = event.target;
    const uploadedFile = inputElement.files && inputElement.files[0];

    if (!uploadedFile) {
      handleCancelCrmAnalysis();
      return;
    }

    crmAbortRef.current = true;
    if (crmReaderRef.current) {
      try {
        crmReaderRef.current.abort();
      } catch (err) {
        // ignore
      }
    }

    crmAbortRef.current = false;
    setCrmFile(null);
    setCrmRows(null);
    setCrmError('');
    setCrmProgress({
      isAnalyzing: true,
      percent: 5,
      stageText: 'Reading file...',
      fileName: uploadedFile.name,
      fileSizeText: formatBytes(uploadedFile.size),
    });
    setFileInfo(`Analyzing ${uploadedFile.name}...`);

    const reader = new FileReader();
    crmReaderRef.current = reader;

    reader.onprogress = (e) => {
      if (crmAbortRef.current) return;
      if (e.lengthComputable && e.total > 0) {
        const readPercent = Math.min(30, Math.round((e.loaded / e.total) * 30));
        setCrmProgress((prev) => ({
          ...prev,
          percent: Math.max(prev.percent, readPercent),
          stageText: `Reading file (${formatBytes(e.loaded)} / ${formatBytes(e.total)})...`,
        }));
      }
    };

    reader.onerror = () => {
      if (crmAbortRef.current) return;
      setCrmProgress({ isAnalyzing: false, percent: 0, stageText: '', fileName: '', fileSizeText: '' });
      setCrmError('Failed to read the file.');
      setMessage('Failed to read the file.');
    };

    reader.onload = async (e) => {
      if (crmAbortRef.current) return;
      setCrmProgress((prev) => ({
        ...prev,
        percent: 32,
        stageText: 'Validating header...',
      }));
      await new Promise((r) => setTimeout(r, 0));
      if (crmAbortRef.current) return;

      const content = e.target.result || '';
      const sanitizedContent = content.replace(/^\uFEFF/, '');
      const lines = sanitizedContent.split(/\r\n|\n|\r/).filter((row) => row.trim() !== '');

      // Validate:
      // 1. Must contain at least a header row
      // 2. Header must have only 1 column
      // 3. Header column name must be "id" (case-insensitive)
      let isHeaderValid = false;
      if (lines.length > 0) {
        const headerCols = parseCsvLine(lines[0]);
        if (headerCols.length === 1 && cleanToken(headerCols[0]).toLowerCase() === 'id') {
          isHeaderValid = true;
        }
      }

      if (!isHeaderValid) {
        // Reject the file
        setCrmFile(null);
        setCrmRows(null);
        if (crmInputRef.current) {
          crmInputRef.current.value = '';
        }
        const errorMsg = 'Invalid Measurement Header CSV: The expected format is a single column with "id" as the header.';
        setMessage(errorMsg);
        setCrmError(errorMsg);
        setCrmProgress({ isAnalyzing: false, percent: 0, stageText: '', fileName: '', fileSizeText: '' });
        const measPart = measurementFile ? `${measurementFile.name} (${measurementRows ?? '...' } rows)` : '';
        setFileInfo(measPart);
        return;
      }

      // Header is valid, now validate data rows in non-blocking batches
      const totalRows = lines.length - 1;
      let allRowsSingleColumn = true;
      const BATCH_SIZE = 5000;

      for (let i = 1; i < lines.length; i += BATCH_SIZE) {
        if (crmAbortRef.current) return;
        const batchEnd = Math.min(lines.length, i + BATCH_SIZE);
        for (let j = i; j < batchEnd; j++) {
          if (parseCsvLine(lines[j]).length !== 1) {
            allRowsSingleColumn = false;
            break;
          }
        }
        if (!allRowsSingleColumn) break;

        const processed = batchEnd - 1;
        const progressPct = totalRows > 0 ? 35 + Math.round((processed / totalRows) * 60) : 95;
        setCrmProgress((prev) => ({
          ...prev,
          percent: Math.min(95, progressPct),
          stageText:
            totalRows > 1000
              ? `Validating row ${formatNumber(processed)} of ${formatNumber(totalRows)}...`
              : 'Validating rows...',
        }));

        if (batchEnd < lines.length) {
          await new Promise((r) => setTimeout(r, 0));
        }
      }

      if (crmAbortRef.current) return;

      if (!allRowsSingleColumn) {
        // Reject the file
        setCrmFile(null);
        setCrmRows(null);
        if (crmInputRef.current) {
          crmInputRef.current.value = '';
        }
        const errorMsg = 'Invalid Measurement Header CSV: The expected format is a single column with "id" as the header.';
        setMessage(errorMsg);
        setCrmError(errorMsg);
        setCrmProgress({ isAnalyzing: false, percent: 0, stageText: '', fileName: '', fileSizeText: '' });
        const measPart = measurementFile ? `${measurementFile.name} (${measurementRows ?? '...' } rows)` : '';
        setFileInfo(measPart);
        return;
      }

      // Valid file
      setCrmProgress({ isAnalyzing: false, percent: 100, stageText: 'Complete', fileName: '', fileSizeText: '' });
      setCrmFile(uploadedFile);
      setCrmError('');
      setMessage('');
      setCrmRows(totalRows);
      const measPart = measurementFile ? `${measurementFile.name} (${measurementRows ?? '...' } rows)` : '';
      setFileInfo(`${uploadedFile.name} loaded with ${totalRows} rows${measPart ? ' · ' + measPart : ''}`);
    };

    reader.readAsText(uploadedFile);
  };

  // Close confirmation modal on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showConfirmModal) {
        setShowConfirmModal(false);
      }
    };
    if (showConfirmModal) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showConfirmModal]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isButtonDisabled) {
      return;
    }
    // Open confirmation popup before proceeding
    setShowConfirmModal(true);
  };

  const handleCancelModal = () => {
    setShowConfirmModal(false);
  };

  const handleProceedConfirm = () => {
    setShowConfirmModal(false);
    setMessage('Rescoring process confirmed.');
  };

  return (
    <div className="App">
      <header className="app-header-bar">
        <div className="app-title-group">
          <h1>Measurement Rescoring Tool</h1>
          <span className="app-subtitle">Batch scorecard updates and measurement line corrections</span>
        </div>
      </header>

      <main className="app-main-grid">
        {/* LEFT COLUMN: Update Requirements & Process Guidelines */}
        <section className="left-column">
          <div className="explanation-card">
            <div className="explanation-header">
              <span className="explanation-icon" aria-hidden>🛈</span>
              <div>
                <h2 className="explanation-title">Update Requirements</h2>
                <span className="explanation-subtitle">Workflow rules &amp; CSV specifications</span>
              </div>
            </div>

            <div className="explanation-body">
              <p className="explanation-intro">
                Rescoring can be performed in one or two steps. Select the action on the right and optionally upload measurement line updates that apply before rescoring.
              </p>

              <div className="workflow-card">
                <div className="workflow-title">
                  <span className="step-badge">1</span>
                  <span>One-step process</span>
                </div>
                <p>
                  Upload a <strong>CRM Measurement Header CSV</strong> containing a single column named <code>id</code>. Choose the rescoring action and submit for rescoring.
                </p>
              </div>

              <div className="workflow-card">
                <div className="workflow-title">
                  <span className="step-badge">2</span>
                  <span>Two-step process</span>
                </div>
                <p>
                  If measurement values need correction, also upload a <strong>Measurement lines CSV</strong> (must include <code>uuid</code> column and at least one measurement column). Lines are processed first, then headers are rescored.
                </p>
              </div>

              <div className="order-sequence">
                <div className="order-step">
                  <span className="order-num">1</span> Lines CSV updates values
                </div>
                <span className="order-arrow">→</span>
                <div className="order-step">
                  <span className="order-num">2</span> Header CSV rescores
                </div>
              </div>

              <div className="explanation-note">
                <span className="note-icon">💡</span>
                <span><strong>Note:</strong> Uploading only a Measurement lines CSV updates lines without rescoring headers.</span>
              </div>
            </div>
          </div>
        </section>

        {/* RIGHT COLUMN: Rescore Setup & Execution */}
        <section className="right-column">
          <form className="rescore-form" onSubmit={handleSubmit}>
            {/* Step 1: Rescoring Action */}
            <div className="form-section">
              <div className="section-label-row">
                <span className="section-step-num">Step 1</span>
                <span className="section-title">Select Rescoring Action</span>
                {!action ? (
                  <span className="badge-required">Action Required</span>
                ) : (
                  <span className="badge-ready">Selected</span>
                )}
              </div>

              <div className="action-options-grid">
                <label className={`action-card ${action === 'update_existing' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="action"
                    value="update_existing"
                    checked={action === 'update_existing'}
                    onChange={(e) => setAction(e.target.value)}
                  />
                  <div className="action-card-content">
                    <span className="action-title">Update existing scorecard(s)</span>
                    <span className="action-desc">Update existing score card(s) instead of creating new ones.</span>
                  </div>
                </label>

                <label className={`action-card ${action === 'create_new' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="action"
                    value="create_new"
                    checked={action === 'create_new'}
                    onChange={(e) => setAction(e.target.value)}
                  />
                  <div className="action-card-content">
                    <span className="action-title">Create new scorecard(s)</span>
                    <span className="action-desc">Create new score card(s) instead of updating existing ones.</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Step 2: CSV Uploads */}
            <div className="form-section">
              <div className="section-label-row">
                <span className="section-step-num">Step 2</span>
                <span className="section-title">Upload CSV Files</span>
              </div>

              <div className="uploads-grid">
                {/* CRM Measurement Header CSV */}
                <label
                  className={`upload-card ${crmProgress.isAnalyzing ? 'is-analyzing' : ''} ${crmFile ? 'has-file' : ''} ${crmError ? 'has-error' : ''}`}
                >
                  <input
                    ref={crmInputRef}
                    type="file"
                    onChange={handleCrmFileChange}
                    accept=".csv"
                    disabled={crmProgress.isAnalyzing}
                  />
                  {crmProgress.isAnalyzing ? (
                    <div className="upload-analyzing" data-testid="crm-analyzing">
                      <div className="analyzing-header">
                        <span className="analyzing-spinner" aria-hidden="true" />
                        <span className="analyzing-title">Analyzing CRM Header...</span>
                      </div>
                      <div className="analyzing-filename" title={crmProgress.fileName}>
                        {crmProgress.fileName} {crmProgress.fileSizeText ? `(${crmProgress.fileSizeText})` : ''}
                      </div>
                      <div className="progress-box">
                        <div className="progress-meta-row">
                          <span className="progress-stage" title={crmProgress.stageText}>{crmProgress.stageText}</span>
                          <span className="progress-percent">{crmProgress.percent}%</span>
                        </div>
                        <div
                          className="progress-track"
                          role="progressbar"
                          aria-label="CRM Header CSV analysis progress"
                          aria-valuenow={crmProgress.percent}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        >
                          <div className="progress-fill" style={{ width: `${crmProgress.percent}%` }} />
                        </div>
                      </div>
                      <button
                        type="button"
                        className="cancel-analysis-btn"
                        onClick={handleCancelCrmAnalysis}
                        aria-label="Cancel CRM file analysis"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : crmFile ? (
                    <div className="upload-fileinfo">
                      <span className="upload-badge success">CRM Header Loaded</span>
                      <div className="upload-filename" title={crmFile.name}>{crmFile.name}</div>
                      <div className="upload-rows">{crmRows ?? '...'} rows</div>
                      <div className="upload-note">Single <code>id</code> column</div>
                    </div>
                  ) : (
                    <div className="upload-prompt">
                      <div className="upload-icon" aria-hidden>{crmError ? '⚠️' : '📥'}</div>
                      <div className="upload-title">CRM Header CSV</div>
                      {crmError ? (
                        <div className="upload-error-text">Expected single column with "id"</div>
                      ) : (
                        <div className="upload-sub">Single <code>id</code> column</div>
                      )}
                      <span className={`upload-action-btn ${crmError ? 'error-btn' : ''}`}>Choose file</span>
                    </div>
                  )}
                </label>

                {/* Measurement Lines CSV */}
                <label
                  className={`upload-card ${measurementProgress.isAnalyzing ? 'is-analyzing' : ''} ${measurementFile ? 'has-file' : ''} ${measurementError ? 'has-error' : ''}`}
                >
                  <input
                    ref={measurementInputRef}
                    type="file"
                    onChange={handleMeasurementFileChange}
                    accept=".csv"
                    disabled={measurementProgress.isAnalyzing}
                  />
                  {measurementProgress.isAnalyzing ? (
                    <div className="upload-analyzing" data-testid="measurement-analyzing">
                      <div className="analyzing-header">
                        <span className="analyzing-spinner" aria-hidden="true" />
                        <span className="analyzing-title">Analyzing Lines CSV...</span>
                      </div>
                      <div className="analyzing-filename" title={measurementProgress.fileName}>
                        {measurementProgress.fileName} {measurementProgress.fileSizeText ? `(${measurementProgress.fileSizeText})` : ''}
                      </div>
                      <div className="progress-box">
                        <div className="progress-meta-row">
                          <span className="progress-stage" title={measurementProgress.stageText}>{measurementProgress.stageText}</span>
                          <span className="progress-percent">{measurementProgress.percent}%</span>
                        </div>
                        <div
                          className="progress-track"
                          role="progressbar"
                          aria-label="Measurement Lines CSV analysis progress"
                          aria-valuenow={measurementProgress.percent}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        >
                          <div className="progress-fill" style={{ width: `${measurementProgress.percent}%` }} />
                        </div>
                      </div>
                      <button
                        type="button"
                        className="cancel-analysis-btn"
                        onClick={handleCancelMeasurementAnalysis}
                        aria-label="Cancel Measurement Lines file analysis"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : measurementFile ? (
                    <div className="upload-fileinfo">
                      <span className="upload-badge success">Lines CSV Loaded</span>
                      <div className="upload-filename" title={measurementFile.name}>{measurementFile.name}</div>
                      <div className="upload-rows">{measurementRows ?? '...'} rows</div>
                      <div className="upload-note">Must include <code>uuid</code></div>
                    </div>
                  ) : (
                    <div className="upload-prompt">
                      <div className="upload-icon" aria-hidden>{measurementError ? '⚠️' : '📤'}</div>
                      <div className="upload-title">Measurement Lines CSV</div>
                      {measurementError ? (
                        <div className="upload-error-text">Expected "uuid" &amp; measurement column(s)</div>
                      ) : (
                        <div className="upload-sub">Optional · Has <code>uuid</code></div>
                      )}
                      <span className={`upload-action-btn ${measurementError ? 'error-btn' : 'optional'}`}>Choose file</span>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Step 3: Run Rescore Action */}
            <div className="form-footer">
              <button type="submit" className="submit-button" disabled={isButtonDisabled}>
                {isAnalyzing ? 'Analyzing CSV...' : 'Run Rescore'}
              </button>
              {fileInfo && <div className="file-info-bar" title={fileInfo}>{fileInfo}</div>}
            </div>
          </form>

          {message && (
            <div
              className={`message-toast ${
                message.toLowerCase().includes('started') || message.toLowerCase().includes('confirmed')
                  ? 'success'
                  : 'error'
              }`}
            >
              {message}
            </div>
          )}
        </section>
      </main>

      {/* Confirmation Modal Popup */}
      {showConfirmModal && (
        <div
          className="modal-backdrop"
          onClick={handleCancelModal}
          data-testid="confirmation-modal-backdrop"
        >
          <div
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-title-group">
                <span className="modal-icon" aria-hidden>⚠️</span>
                <h2 id="modal-title" className="modal-title">Confirm Rescoring</h2>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={handleCancelModal}
                aria-label="Close confirmation dialog"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-prompt-text">
                Are you sure you want to proceed with the rescoring process?
              </p>
              <div className="modal-summary-box">
                <div className="modal-summary-item">
                  <span className="modal-summary-label">Selected Action:</span>
                  <span className="modal-summary-value">
                    {action === 'update_existing'
                      ? 'Update existing scorecard(s)'
                      : 'Create new scorecard(s)'}
                  </span>
                </div>
                <div className="modal-summary-item">
                  <span className="modal-summary-label">CRM Header CSV:</span>
                  <span className="modal-summary-value">
                    {crmFile ? `${crmFile.name} (${crmRows ?? '0'} rows)` : 'Not provided'}
                  </span>
                </div>
                <div className="modal-summary-item">
                  <span className="modal-summary-label">Measurement Lines CSV:</span>
                  <span className="modal-summary-value">
                    {measurementFile ? `${measurementFile.name} (${measurementRows ?? '0'} rows)` : 'Not provided'}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="modal-btn modal-btn-secondary"
                onClick={handleCancelModal}
              >
                No
              </button>
              <button
                type="button"
                className="modal-btn modal-btn-primary"
                onClick={handleProceedConfirm}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
